import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import * as analysisState from '@/components/Analysis/state';
import type { AssemblyTemplateQuery } from '@/types';
import type {
  AllDecksForFilterSideBarQuery,
  AllPaintRegionsForFilterAndSearchPanelQuery,
  AllWorkpacksForFilterSideBarQuery,
} from '@/__generated__/graphql';
import type { FilterField, FilterPill } from './InsightsSearchBar.types';
import { SPATIAL_DECKS, SPATIAL_PAINT, SPATIAL_WORKPACK } from './constants';

/** Regex to detect range expressions like ">50", "<=100", "=10-20" */
const RANGE_EXPRESSION_PATTERN = /[><=!]|=\s*\d+(\.\d+)?\s*-\s*\d+(\.\d+)?/;

type SpatialType = 'deck' | 'paintRegion' | 'workpack';
type SetActiveFieldId = (update: string | null | ((prev: string | null) => string | null)) => void;

interface UseSearchBarHandlersParams {
  filterFields: FilterField[];
  assemblyTemplate: AssemblyTemplateQuery | undefined;
  decksData: AllDecksForFilterSideBarQuery | undefined;
  paintRegionsData: AllPaintRegionsForFilterAndSearchPanelQuery | undefined;
  workpacksData: AllWorkpacksForFilterSideBarQuery | undefined;
  activeFieldId: string | null;
  setActiveFieldId: SetActiveFieldId;
  addTemplateOption: (fieldKey: string, value: string) => void;
  addDeckFilter: (id: string) => void;
  addPaintRegionFilter: (id: string) => void;
  addWorkpackFilter: (id: string) => void;
  setApplyFilters: (value: boolean) => void;
  setSearchTerm: (term: string | ((prev: string) => string)) => void;
  setRangeSearchTerm: (term: string) => void;
  removePill: (pill: FilterPill) => void;
}

export function useSearchBarHandlers({
  filterFields,
  assemblyTemplate,
  decksData,
  paintRegionsData,
  workpacksData,
  activeFieldId,
  setActiveFieldId,
  addTemplateOption,
  addDeckFilter,
  addPaintRegionFilter,
  addWorkpackFilter,
  setApplyFilters,
  setSearchTerm,
  setRangeSearchTerm,
  removePill,
}: UseSearchBarHandlersParams) {
  const setRefetchEquipmentTable = useSetAtom(analysisState.refetchEquipmentTable);

  const triggerTableRefetch = useCallback(() => {
    setRefetchEquipmentTable((count) => (count ?? 0) + 1);
  }, [setRefetchEquipmentTable]);

  const closeDropdown = useCallback(
    (
      setActiveFieldName: (name: string | null) => void,
      setInputValue: (value: string) => void,
      setOpen: (open: boolean) => void
    ) => {
      setActiveFieldId(null);
      setActiveFieldName(null);
      setInputValue('');
      setOpen(false);
    },
    [setActiveFieldId]
  );

  const addSpatialFilter = useCallback(
    (type: SpatialType, id: string) => {
      const handlers: Record<SpatialType, (id: string) => void> = {
        deck: addDeckFilter,
        paintRegion: addPaintRegionFilter,
        workpack: addWorkpackFilter,
      };
      handlers[type](id);
    },
    [addDeckFilter, addPaintRegionFilter, addWorkpackFilter]
  );

  const findSpatialEntityId = useCallback(
    (type: SpatialType, searchValue: string): string | null => {
      const dataSources: Record<SpatialType, { id: string; name?: string }[] | undefined> = {
        deck: decksData?.allDecks,
        paintRegion: paintRegionsData?.allPaintRegions,
        workpack: workpacksData?.allWorkpacks,
      };

      const entities = dataSources[type];
      if (!entities) return null;

      const normalizedSearch = searchValue.toLowerCase();
      const match = entities.find(
        (item) => item.id === searchValue || item.name?.toLowerCase() === normalizedSearch
      );

      return match?.id ?? null;
    },
    [decksData, paintRegionsData, workpacksData]
  );

  const resolveSpatialFilter = useCallback(
    (type: SpatialType, value: string) => {
      const entityId = findSpatialEntityId(type, value);
      if (entityId) {
        addSpatialFilter(type, entityId);
        return true;
      }

      // Try parsing as numeric ID
      const numericId = Number.parseInt(value, 10);
      if (!Number.isNaN(numericId)) {
        addSpatialFilter(type, String(numericId));
        return true;
      }

      return false;
    },
    [findSpatialEntityId, addSpatialFilter]
  );

  const findMatchingField = useCallback(
    (fieldName: string): FilterField | undefined => {
      const normalized = fieldName.toLowerCase();
      return filterFields.find((field) => {
        if (field.name?.toLowerCase() === normalized) return true;
        if (field.id?.toLowerCase() === normalized) return true;
        if (field.templateField) {
          const key = field.templateField.key?.toLowerCase();
          const id = field.templateField.id?.toLowerCase();
          return key === normalized || id === normalized;
        }
        return false;
      });
    },
    [filterFields]
  );

  const handleShowFilterOptions = useCallback(
    (
      fieldId: string,
      fieldName: string,
      setActiveFieldName: (name: string | null) => void,
      setInputValue: (value: string) => void,
      setOpen: (open: boolean) => void
    ) => {
      setActiveFieldId((prev) => (prev === fieldId ? null : fieldId));
      setActiveFieldName(fieldName);
      setInputValue('');
      setOpen(true);
    },
    [setActiveFieldId]
  );

  const handleSelectOption = useCallback(
    (activeField: FilterField | undefined, optionId: string, closeDropdownFn: () => void) => {
      if (!activeField) return;

      if (activeField.type === 'template') {
        const field = assemblyTemplate?.fields?.find((f) => f.id === activeFieldId);
        if (field) {
          const fieldKey = field.key ?? field.id;
          addTemplateOption(fieldKey, optionId);
        }
      } else if (activeField.id === SPATIAL_DECKS) {
        addDeckFilter(optionId);
      } else if (activeField.id === SPATIAL_PAINT) {
        addPaintRegionFilter(optionId);
      } else if (activeField.id === SPATIAL_WORKPACK) {
        addWorkpackFilter(optionId);
      }

      closeDropdownFn();
      setApplyFilters(true);
    },
    [
      activeFieldId,
      assemblyTemplate,
      addTemplateOption,
      addDeckFilter,
      addPaintRegionFilter,
      addWorkpackFilter,
      setApplyFilters,
    ]
  );

  const handleSearchSubmit = useCallback(
    (value: string, closeDropdownFn: () => void) => {
      if (RANGE_EXPRESSION_PATTERN.test(value)) {
        setRangeSearchTerm(value);
      } else {
        setSearchTerm(value);
      }
      closeDropdownFn();
      triggerTableRefetch();
    },
    [setSearchTerm, setRangeSearchTerm, triggerTableRefetch]
  );

  const applyFieldFilter = useCallback(
    (field: FilterField, value: string) => {
      if (field.type === 'template') {
        const key = field.templateField?.key ?? field.templateField?.id ?? field.id;
        addTemplateOption(key, value);
      } else if (field.id === SPATIAL_DECKS) {
        resolveSpatialFilter('deck', value);
      } else if (field.id === SPATIAL_PAINT) {
        resolveSpatialFilter('paintRegion', value);
      } else if (field.id === SPATIAL_WORKPACK) {
        resolveSpatialFilter('workpack', value);
      } else {
        addTemplateOption(field.id, value);
      }
    },
    [addTemplateOption, resolveSpatialFilter]
  );

  const handleSaveEditedPill = useCallback(
    (pill: FilterPill, editedValue: string, closeDropdownFn: () => void) => {
      const colonIndex = editedValue.indexOf(':');
      const hasFieldPrefix = colonIndex >= 0;
      const fieldName = hasFieldPrefix ? editedValue.slice(0, colonIndex).trim() : null;
      const filterValue = hasFieldPrefix
        ? editedValue.slice(colonIndex + 1).trim()
        : editedValue.trim();

      removePill(pill);

      // User changed the field name
      if (fieldName && fieldName !== pill.fieldName) {
        const matchedField = findMatchingField(fieldName);
        if (matchedField) {
          applyFieldFilter(matchedField, filterValue);
          setApplyFilters(true);
          closeDropdownFn();
          return;
        }

        // Field not found - treat entire input as search term
        console.warn(`Field "${fieldName}" not recognized; using as search term`);
        setSearchTerm(editedValue);
        triggerTableRefetch();
        closeDropdownFn();
        return;
      }

      // Same field, just value changed
      switch (pill.type) {
        case 'template': {
          addTemplateOption(pill.fieldKey, filterValue);
          break;
        }
        case 'searchTerm': {
          setSearchTerm((prev) => {
            const terms = (prev ?? '')
              .split('||')
              .map((t) => t.trim())
              .filter(Boolean);
            const index = terms.indexOf(pill.value);
            if (index === -1) return filterValue;
            terms[index] = filterValue;
            return terms.join('||');
          });
          triggerTableRefetch();
          break;
        }
        case 'rangeSearchTerm': {
          setRangeSearchTerm(filterValue);
          break;
        }
        case 'deck': {
          if (!resolveSpatialFilter('deck', filterValue)) {
            setSearchTerm(filterValue);
            triggerTableRefetch();
          }
          break;
        }
        case 'paintRegion': {
          if (!resolveSpatialFilter('paintRegion', filterValue)) {
            setSearchTerm(filterValue);
            triggerTableRefetch();
          }
          break;
        }
        case 'workpack': {
          if (!resolveSpatialFilter('workpack', filterValue)) {
            setSearchTerm(filterValue);
            triggerTableRefetch();
          }
          break;
        }
      }

      setApplyFilters(true);
      closeDropdownFn();
    },
    [
      removePill,
      findMatchingField,
      applyFieldFilter,
      addTemplateOption,
      setSearchTerm,
      setRangeSearchTerm,
      setApplyFilters,
      resolveSpatialFilter,
      triggerTableRefetch,
    ]
  );

  return {
    closeDropdown,
    handleShowFilterOptions,
    handleSelectOption,
    handleSearchSubmit,
    handleSaveEditedPill,
    triggerTableRefetch,
  };
}
